import {
  HttpHeaders,
  type HttpClientConfig,
  type HttpClientInterface,
} from 'core/infra/http/HttpClient'

import { type ConfirmAwardedLaneAction } from '../domain/ConfirmAwardedLaneAction'
import { type DistanceUnit } from '../domain/DistanceUnit'
import { type LanePreference } from '../domain/LanePreferences'
import { type Proposal } from '../domain/Proposal'
import { CARRIER_INVITES_ROOT } from './createRfpGuideInviteInterceptor'
import {
  mapApiToAutoSubmit,
  mapApiToAwardConfirmationAutoSubmit,
  mapApiToLaneProposal,
  mapApiToProposal,
  type RawAutoSubmit,
  type RawAwardConfirmationAutoSubmit,
  type RawLaneProposal,
  type RawProposal,
} from './ProposalMapper'
import { mapApiToRfpSummary, type RawRfpSummary } from './RfpSummaryMapper'

const BASE_URL = CARRIER_INVITES_ROOT
const RFP_V2_PROPOSALS_ROOT = 'api/v2/rfp/proposals'

type ProposalRepositoryDependencies = {
  httpClient: HttpClientInterface
}

type CreateLaneProposalParams = {
  capacity: number
  flatRate: number
  perDistanceRate: number
  laneId: number
  rfpId: string
  distanceUnit: DistanceUnit
  preference?: LanePreference
}

type UpdateLaneProposalParams = {
  capacity: number
  flatRate: number
  laneProposalId: number
  laneId: number
  perDistanceRate: number
  rfpId: string
  distanceUnit: DistanceUnit
  preference?: LanePreference
}

type DeleteLaneProposalParams = {
  laneProposalId: number
  rfpId: string
}

type UploadRfpLanesFileParams = {
  rfpId: string
  rateType: string
  file: File
}

type UpdateAwardedLanesParams = {
  rfpId: string
  laneProposalIds: Array<number>
  action: ConfirmAwardedLaneAction
}

type CreateOrUpdateLaneProposalPayload = {
  capacity: number
  flat_rate: number
  lane: number
  per_mile_rate: number
  lane_preference?: string
}

export class ProposalRepository {
  private readonly httpClient: HttpClientInterface

  constructor({ httpClient }: ProposalRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getProposalById(
    id: string,
    queryParams: string | null,
    config?: Partial<HttpClientConfig>
  ): Promise<Proposal> {
    let endpoint = `${BASE_URL}/${id}`
    if (queryParams) endpoint = `${endpoint}?${queryParams}`

    const response = await this.httpClient.get<RawProposal>(endpoint, config)

    return mapApiToProposal(response.data)
  }

  async downloadAttachments(id: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/${id}/bulk_rfp_attachment_download`

    const { data } = await this.httpClient.get(endpoint, {
      ...config,
      responseType: 'blob',
    })

    return data
  }

  async downloadRfpLanesTemplateFile(id: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/${id}/download`

    const { data } = await this.httpClient.get(endpoint, {
      ...config,
      responseType: 'blob',
    })

    return data
  }

  async createLaneProposal(
    saveParams: CreateLaneProposalParams,
    config: Partial<HttpClientConfig>
  ) {
    const { rfpId, capacity, flatRate, laneId, perDistanceRate, distanceUnit, preference } =
      saveParams

    const endpoint = `${BASE_URL}/${rfpId}/lane_proposals`

    // We decided to send to the backend the perDistanceRate as per_mile_rate
    // no matter the distance type. We did so to make the BE and FE easier to change.
    // This is why this reassign here.
    const payload: CreateOrUpdateLaneProposalPayload = {
      capacity: capacity,
      flat_rate: flatRate,
      lane: laneId,
      per_mile_rate: perDistanceRate,
    }

    if (preference) {
      payload.lane_preference = preference.id
    }

    const { data } = await this.httpClient.post<RawLaneProposal>(endpoint, payload, config)

    return mapApiToLaneProposal(data, distanceUnit)
  }

  async updateLaneProposal(
    updateParams: UpdateLaneProposalParams,
    config: Partial<HttpClientConfig>
  ) {
    const {
      rfpId,
      laneProposalId,
      capacity,
      flatRate,
      laneId,
      perDistanceRate,
      distanceUnit,
      preference,
    } = updateParams

    const endpoint = `${BASE_URL}/${rfpId}/lane_proposals/${laneProposalId}`

    const payload: CreateOrUpdateLaneProposalPayload = {
      capacity,
      flat_rate: flatRate,
      lane: laneId,
      per_mile_rate: perDistanceRate,
    }

    if (preference) {
      payload.lane_preference = preference.id
    }

    const { data } = await this.httpClient.put<RawLaneProposal>(endpoint, payload, config)

    return mapApiToLaneProposal(data, distanceUnit)
  }

  async deleteLaneProposal(
    deleteParams: DeleteLaneProposalParams,
    config: Partial<HttpClientConfig>
  ) {
    const { laneProposalId, rfpId } = deleteParams

    const endpoint = `${BASE_URL}/${rfpId}/lane_proposals/${laneProposalId}`

    await this.httpClient.delete<void>(endpoint, config)

    return undefined
  }

  async submitProposalV2(id: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${RFP_V2_PROPOSALS_ROOT}/invites/${id}/submit`

    await this.httpClient.post(endpoint, config)
  }

  async withdrawProposal(id: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/${id}/unsubmit`

    await this.httpClient.post(endpoint, config)
  }

  async toggleAutoSubmitProposal(
    id: string,
    autoSubmit: boolean,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${BASE_URL}/${id}/toggle_auto_submit`

    const payload = { auto_submit: autoSubmit }

    const { data } = await this.httpClient.post<RawAutoSubmit>(endpoint, payload, config)

    return mapApiToAutoSubmit(data)
  }

  async getRfpHistory(config?: Partial<HttpClientConfig>) {
    const endpoint = `${RFP_V2_PROPOSALS_ROOT}/carriers/rfp`

    const { data } = await this.httpClient.get<Array<RawRfpSummary>>(endpoint, {
      ...config,
      timeout: 60000,
    })

    return data.map(mapApiToRfpSummary)
  }

  async uploadRfpLanesFileV2(
    { rfpId, file, rateType }: UploadRfpLanesFileParams,
    config?: Partial<HttpClientConfig>
  ): Promise<void> {
    const endpoint = `${RFP_V2_PROPOSALS_ROOT}/invites/${rfpId}/upload`

    const formData = new FormData()
    formData.append('file', file)
    formData.append('rate_type', rateType)

    await this.httpClient.post(endpoint, formData, {
      ...config,
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    })
  }

  async updateAwardedLanes(
    { rfpId, laneProposalIds, action }: UpdateAwardedLanesParams,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${BASE_URL}/award-confirmation/${rfpId}/update_lanes`
    const payload = { action, lane_proposals_ids: laneProposalIds }

    await this.httpClient.post(endpoint, payload, config)
  }

  async toggleAwardAutoSubmit(
    rfpId: string,
    autoSubmit: boolean,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${BASE_URL}/${rfpId}/toggle_award_confirmation_auto_submit`

    const payload = { award_confirmation_auto_submit: autoSubmit }

    const { data } = await this.httpClient.post<RawAwardConfirmationAutoSubmit>(
      endpoint,
      payload,
      config
    )

    return mapApiToAwardConfirmationAutoSubmit(data)
  }

  async submitAwardConfirmation(rfpId: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/award-confirmation/${rfpId}/submit_award_confirmation`

    // since we are always updating the lanes manually,
    // the accept_all payload is always false
    const payload = { accept_all: false }

    await this.httpClient.post(endpoint, payload, config)
  }
}
